import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraDataTable, SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton, SpecraInput, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraCheckboxField, SpecraCombobox, SpecraFormField, SpecraInlineBanner, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { projectDataClient } from '../../../../lib/connect/clients';
import { applyCompetitiveAdjustment } from '../../../../lib/utils/pricingAdjustment';
import { money } from './ratesView';
import { useSetPricingAdjustment } from './usePricingRateBook';
const T = Testids.pricingWorkspace;
export function CompetitiveAdjustmentDialog({ scope, token, periods, categories, adjustments, cell, onClose, onReload }) {
    const [appliesTo, setAppliesTo] = useState(cell ? 'cell' : 'scenario');
    const [period, setPeriod] = useState(scope.period);
    const [category, setCategory] = useState(cell || categories.find((c)=>!c.removedFromSectionB)?.lcatLogicalKey || '');
    const periodKey = [
        'period',
        'cell'
    ].includes(appliesTo) ? period : '';
    const lcatLogicalKey = [
        'category',
        'cell'
    ].includes(appliesTo) ? category : '';
    const entry = adjustments.find((a)=>a.scope === appliesTo && (a.periodKey || '') === periodKey && (a.lcatLogicalKey || '') === lcatLogicalKey);
    const [pct, setPct] = useState(entry?.pct || '');
    const [reason, setReason] = useState('');
    const [reapply, setReapply] = useState(false);
    const write = useSetPricingAdjustment();
    const books = useQueries({
        queries: periods.map((p)=>({
                queryKey: [
                    'pricing-ratebook',
                    scope.principal,
                    scope.projectId,
                    scope.scenarioId,
                    p.periodKey
                ],
                retry: false,
                queryFn: ()=>projectDataClient.getPricingRateBook({
                        projectId: scope.projectId,
                        scenarioId: scope.scenarioId,
                        periodKey: p.periodKey
                    })
            }))
    });
    const validPct = pct === '' || /^-?\d{1,2}(\.\d{1,2})?$/.test(pct) && Math.abs(Number(pct)) <= 50;
    const validScope = (!periodKey || periods.some((p)=>p.periodKey === periodKey)) && (![
        'category',
        'cell'
    ].includes(appliesTo) || categories.some((c)=>c.lcatLogicalKey === category && !c.removedFromSectionB));
    const policy = {
        id: entry?.id || 'preview',
        scope: appliesTo,
        periodKey,
        lcatLogicalKey,
        pct,
        reason,
        setBy: '',
        setAt: ''
    };
    const without = adjustments.filter((a)=>a !== entry);
    const policies = pct && Number(pct) !== 0 ? [
        ...without,
        policy
    ] : without;
    const previews = books.flatMap((book, i)=>(book.data?.rows || []).flatMap((row)=>{
            const cat = categories.find((c)=>c.id === row.categoryId);
            if (!cat || cat.removedFromSectionB || !cat.lcatLogicalKey) return [];
            const p = periods[i];
            const winning = applyCompetitiveAdjustment('1', false, policies, cat.lcatLogicalKey, p.periodKey).applied;
            const old = applyCompetitiveAdjustment('1', false, adjustments, cat.lcatLogicalKey, p.periodKey).applied;
            const governed = winning?.id === policy.id || (!pct || Number(pct) === 0) && !!entry && old?.id === entry.id;
            if (!governed) return [];
            const adjusted = applyCompetitiveAdjustment(row.suggested?.amount || '', row.suggested?.requiresChoice || false, policies, cat.lcatLogicalKey, p.periodKey).adjusted;
            return [
                {
                    id: `${row.categoryId}@${p.periodKey}`,
                    title: row.title,
                    period: p.label,
                    suggested: row.suggested?.amount || '',
                    adjusted: adjusted || (row.suggested?.requiresChoice ? '' : row.suggested?.amount || ''),
                    reapply: row.using?.basis === 'adjusted_suggestion' && !!row.suggested?.amount && !row.suggested?.requiresChoice
                }
            ];
        }));
    const count = previews.filter((p)=>p.reapply).length;
    const stale = !!write.error && [
        Code.Aborted,
        Code.FailedPrecondition
    ].includes(ConnectError.from(write.error).code);
    const loading = books.some((b)=>b.isPending);
    const loadError = books.find((b)=>b.error)?.error;
    const save = async (remove)=>{
        try {
            await write.mutateAsync({
                projectId: scope.projectId,
                scenarioId: scope.scenarioId,
                scope: appliesTo,
                periodKey,
                lcatLogicalKey,
                pct: remove ? '' : pct,
                reason,
                expectedUpdatedAt: token,
                reapplyToUsing: reapply && count > 0
            });
            onClose();
        } catch  {
        /* inline error */ }
    };
    const disabled = !reason.trim() || !validPct || !validScope || loading || !!loadError || write.isPending || stale;
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        size: "md",
        title: "Team competitive adjustment",
        testId: T.adjustmentDialog,
        onOpenChange: (open)=>{
            if (!open && !write.isPending) onClose();
        },
        footer: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "destructive",
                    "data-testid": T.adjustmentRemove,
                    disabled: disabled || !entry,
                    onClick: ()=>save(true),
                    children: "Remove adjustment"
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    disabled: write.isPending,
                    onClick: onClose,
                    children: "Cancel"
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": T.adjustmentSave,
                    disabled: disabled,
                    onClick: ()=>save(false),
                    children: write.isPending ? 'Saving…' : 'Save'
                })
            ]
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                    label: "Applies to",
                    testId: T.adjustmentScope,
                    value: appliesTo,
                    onValueChange: (value)=>{
                        const next = value;
                        setAppliesTo(next);
                        setReapply(false);
                        const existing = adjustments.find((a)=>a.scope === next && (a.periodKey || '') === ([
                                'period',
                                'cell'
                            ].includes(next) ? period : '') && (a.lcatLogicalKey || '') === ([
                                'category',
                                'cell'
                            ].includes(next) ? category : ''));
                        setPct(existing?.pct || '');
                    },
                    options: [
                        {
                            value: 'scenario',
                            label: 'All labor categories'
                        },
                        {
                            value: 'period',
                            label: 'One period'
                        },
                        {
                            value: 'category',
                            label: 'One labor category'
                        },
                        {
                            value: 'cell',
                            label: 'One cell'
                        }
                    ]
                }),
                [
                    'period',
                    'cell'
                ].includes(appliesTo) && /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Period",
                    htmlFor: "adjustment-period",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        id: "adjustment-period",
                        "data-testid": T.adjustmentPeriod,
                        value: period,
                        onChange: (e)=>{
                            setPeriod(e.target.value);
                            setReapply(false);
                            setPct(adjustments.find((a)=>a.scope === appliesTo && a.periodKey === e.target.value && (a.lcatLogicalKey || '') === lcatLogicalKey)?.pct || '');
                        },
                        children: periods.map((p)=>/*#__PURE__*/ _jsx("option", {
                                value: p.periodKey,
                                children: p.label
                            }, p.periodKey))
                    })
                }),
                [
                    'category',
                    'cell'
                ].includes(appliesTo) && /*#__PURE__*/ _jsx(SpecraCombobox, {
                    label: "Labor category",
                    value: category,
                    options: categories.filter((c)=>!c.removedFromSectionB && !!c.lcatLogicalKey).map((c)=>({
                            value: c.lcatLogicalKey || '',
                            label: c.title
                        })),
                    triggerProps: {
                        'data-testid': T.adjustmentCategory
                    },
                    onValueChange: (v)=>{
                        setCategory(v || '');
                        setReapply(false);
                        setPct(adjustments.find((a)=>a.scope === appliesTo && (a.periodKey || '') === periodKey && a.lcatLogicalKey === v)?.pct || '');
                    }
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Adjustment (%)",
                    htmlFor: "adjustment-pct",
                    hint: "Negative bids under the evidence. Applied to real-rate suggestions only; ceilings are never adjusted.",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        id: "adjustment-pct",
                        "data-testid": T.adjustmentPct,
                        inputMode: "decimal",
                        value: pct,
                        onChange: (e)=>setPct(e.target.value)
                    })
                }),
                !validPct && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    children: "Enter a percentage from −50 to 50 with at most two decimal places."
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Why",
                    required: true,
                    htmlFor: "adjustment-reason",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        id: "adjustment-reason",
                        "data-testid": T.adjustmentReason,
                        value: reason,
                        onChange: (e)=>setReason(e.target.value)
                    })
                }),
                loading ? /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Loading preview…"
                }) : /*#__PURE__*/ _jsx(SpecraDataTable, {
                    rows: previews.slice(0, 10),
                    rowKey: (r)=>r.id,
                    getRowLabel: (r)=>`${r.title} ${r.period}`,
                    columns: [
                        {
                            id: 'title',
                            header: 'LCAT',
                            cell: (r)=>r.title
                        },
                        {
                            id: 'period',
                            header: 'Period',
                            cell: (r)=>r.period
                        },
                        {
                            id: 'suggested',
                            header: 'Suggested',
                            cell: (r)=>money(r.suggested)
                        },
                        {
                            id: 'adjusted',
                            header: 'Adjusted',
                            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                                    title: !r.adjusted ? 'Adjustments apply to real-rate suggestions only.' : undefined,
                                    children: money(r.adjusted)
                                })
                        }
                    ]
                }),
                previews.length > 10 && /*#__PURE__*/ _jsxs(SpecraText, {
                    children: [
                        "+",
                        previews.length - 10,
                        " more"
                    ]
                }),
                count > 0 && /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                    "data-testid": T.adjustmentReapply,
                    label: `Re-apply to ${count} rows that used the old adjustment`,
                    controlProps: {
                        checked: reapply,
                        onCheckedChange: (v)=>setReapply(v === true)
                    }
                }),
                /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "info",
                    children: "Suggested stays the rule's output. This adds a visible team step; Using still needs a reason when it differs from Adjusted."
                }),
                (write.error || loadError) && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "danger",
                    children: [
                        stale ? 'Pricing changed since you loaded it.' : (write.error || loadError)?.message,
                        stale && /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: async ()=>{
                                await onReload();
                                onClose();
                            },
                            children: "Reload"
                        })
                    ]
                })
            ]
        })
    });
}
